import { CompanyProfileFormModel } from 'src/Libs/company-settings/models/company-profile-form.model'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { companyProfileFormToLocationDto } from 'src/Libs/company-settings/transformer'
import { updateCompanyLocationApi } from 'src/Libs/company-settings/api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ApiResult } from 'src/Libs/api'
import { useUser } from 'src/Hooks/useUser'

interface UseCompanyAddressHook {
  /**
   * Update a companies profile
   * A company profile is cumulated of properties from different api sources. So 2 different api's will also triggered
   * when this request is called.
   *
   * @param companyId
   * @param formModel
   */
  updateCompanyProfile(companyId: number, formModel: CompanyProfileFormModel): Promise<void>
}

/**
 * Provides the ability to update a companies profile settings as a cumulated update
 */
const useCompanyProfile = (): UseCompanyAddressHook => {
  const authFetch = useAuthFetchWithResponse()

  const { refreshCompany } = useUser()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const updateLocation = async (companyId: number, formModel: CompanyProfileFormModel): Promise<ApiResult<void>> => {
    const dto = companyProfileFormToLocationDto(formModel)
    return updateCompanyLocationApi(companyId, dto, authFetch)
  }

  const updateCompanyProfile = async (companyId: number, formModel: CompanyProfileFormModel): Promise<void> => {
    try {
      const result = await updateLocation(companyId, formModel)
      if (result.hasErrors) {
        enqueueErrorStack(t('common.putFailed', { item: t('user.company.changes') }))
      } else {
        enqueueSuccessStack(t('common.putSuccess', { item: t('user.company.changes') }))
      }
      refreshCompany()
    } catch (e) {
      return enqueueErrorStack(t('common.putFailed', { item: t('user.company.changes') }))
    }
  }

  return { updateCompanyProfile }
}

export default useCompanyProfile
